import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import {
  Copy,
  Check,
  MousePointer,
  UserPlus,
  RefreshCw,
  DollarSign,
  CreditCard,
  Settings,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  Banknote
} from 'lucide-react';

const AffiliateDashboard = () => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [affiliateData, setAffiliateData] = useState({
    code: '',
    clicks: 0,
    signups: 0,
    conversions: 0,
    earned: 0,
    pendingBalance: 0,
    availableForPayout: 0,
    minimumPayout: 500
  });

  const [payoutSettings, setPayoutSettings] = useState({
    payoutMethod: null,
    paypalEmail: '',
    bankAccountNumber: '',
    bankIban: '',
    bankSwift: '',
    bankAccountHolder: ''
  });

  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [showPayoutForm, setShowPayoutForm] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;
      try {
        // Načíst affiliate statistiky
        const stats = await api.getAffiliateStats(user.id);
        setAffiliateData({
          code: stats.code || '',
          clicks: stats.clicks || 0,
          signups: stats.signups || 0,
          conversions: stats.conversions || 0,
          earned: stats.earned || 0,
          pendingBalance: stats.pendingBalance || 0,
          availableForPayout: stats.availableForPayout || 0,
          minimumPayout: stats.minimumPayout || 500
        });

        // Načíst nastavení výplaty
        const settings = await api.getPayoutSettings(user.id);
        setPayoutSettings({
          payoutMethod: settings.payoutMethod,
          paypalEmail: settings.paypalEmail || '',
          bankAccountNumber: settings.bankAccountNumber || '',
          bankIban: settings.bankIban || '',
          bankSwift: settings.bankSwift || '',
          bankAccountHolder: settings.bankAccountHolder || ''
        });

        // Načíst provize
        const commissionsData = await api.getAffiliateCommissions(user.id);
        setCommissions(commissionsData.commissions || []);

        // Načíst historii výplat
        const payoutsData = await api.getPayoutHistory(user.id);
        setPayouts(payoutsData.payouts || []);

      } catch (error) {
        console.error('Error loading affiliate data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user?.id]);

  const affiliateLink = `https://synapso.cz/?ref=${affiliateData.code}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(affiliateLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleSavePayoutSettings = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await api.updatePayoutSettings(user.id, payoutSettings);
      if (result.success) {
        setMessage({ type: 'success', text: 'Platební údaje byly uloženy.' });
        setShowPayoutForm(false);
      } else {
        setMessage({ type: 'error', text: result.error || 'Nepodařilo se uložit.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Nepodařilo se uložit platební údaje.' });
    } finally {
      setSaving(false);
    }
  };

  const handleRequestPayout = async () => {
    setRequesting(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await api.requestPayout(user.id);
      if (result.success) {
        setMessage({ type: 'success', text: `Žádost o výplatu ${result.amount} Kč byla odeslána.` });
        // Refresh data
        const stats = await api.getAffiliateStats(user.id);
        setAffiliateData(prev => ({
          ...prev,
          availableForPayout: stats.availableForPayout || 0
        }));
        const payoutsData = await api.getPayoutHistory(user.id);
        setPayouts(payoutsData.payouts || []);
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Nepodařilo se odeslat žádost.' });
    } finally {
      setRequesting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      processing: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-green-500/20 text-green-400',
      paid: 'bg-green-500/20 text-green-400',
      failed: 'bg-red-500/20 text-red-400'
    };
    const labels = {
      pending: 'Čeká na schválení',
      approved: 'Schváleno',
      processing: 'Zpracovává se',
      completed: 'Vyplaceno',
      paid: 'Vyplaceno',
      failed: 'Zamítnuto'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-950 pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-950 pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Affiliate Dashboard</h1>
          <p className="text-dark-400">Získej 20% provizi z každého předplatného</p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl flex items-center space-x-3 ${
            message.type === 'success' ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle size={20} className="text-green-400" />
            ) : (
              <AlertCircle size={20} className="text-red-400" />
            )}
            <span className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}>
              {message.text}
            </span>
          </div>
        )}

        {/* Affiliate Link */}
        <div className="glass rounded-2xl p-6 mb-8">
          <h2 className="text-white font-semibold mb-4">Tvůj affiliate odkaz</h2>
          <div className="flex items-center space-x-3">
            <input
              type="text"
              value={affiliateLink}
              readOnly
              className="flex-1 px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 text-sm"
            />
            <button
              onClick={handleCopy}
              className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center space-x-2 ${
                copied ? 'bg-green-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
            >
              {copied ? <Check size={20} /> : <Copy size={20} />}
              <span>{copied ? 'Zkopírováno!' : 'Kopírovat'}</span>
            </button>
          </div>
          <p className="text-dark-500 text-sm mt-3">
            Tvůj kód: <span className="text-primary-400 font-mono">{affiliateData.code}</span>
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass rounded-xl p-5">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <MousePointer size={20} className="text-blue-400" />
              </div>
              <span className="text-dark-400 text-sm">Kliknutí</span>
            </div>
            <p className="text-3xl font-bold text-white">{affiliateData.clicks}</p>
          </div>

          <div className="glass rounded-xl p-5">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <UserPlus size={20} className="text-purple-400" />
              </div>
              <span className="text-dark-400 text-sm">Registrací</span>
            </div>
            <p className="text-3xl font-bold text-white">{affiliateData.signups}</p>
          </div>

          <div className="glass rounded-xl p-5">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <RefreshCw size={20} className="text-green-400" />
              </div>
              <span className="text-dark-400 text-sm">Konverzí</span>
            </div>
            <p className="text-3xl font-bold text-white">{affiliateData.conversions}</p>
          </div>

          <div className="glass rounded-xl p-5">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <DollarSign size={20} className="text-amber-400" />
              </div>
              <span className="text-dark-400 text-sm">Celkem vyděláno</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {affiliateData.earned} <span className="text-lg text-dark-400">Kč</span>
            </p>
          </div>
        </div>

        {/* Balance & Payout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Balance */}
          <div className="glass rounded-2xl p-6">
            <h2 className="text-white font-semibold mb-4">Zůstatek</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-dark-400">Čeká na schválení</span>
                <span className="text-white font-semibold">{affiliateData.pendingBalance} Kč</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-dark-400">K vyplacení</span>
                <span className="text-green-400 font-semibold">{affiliateData.availableForPayout} Kč</span>
              </div>
              <div className="border-t border-dark-700 pt-4">
                <p className="text-dark-500 text-sm">Minimální výplata: {affiliateData.minimumPayout} Kč</p>
              </div>

              <button
                onClick={handleRequestPayout}
                disabled={requesting || affiliateData.availableForPayout < affiliateData.minimumPayout || !payoutSettings.payoutMethod}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {requesting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>Odesílám...</span>
                  </>
                ) : (
                  <>
                    <Banknote size={20} />
                    <span>Požádat o výplatu</span>
                  </>
                )}
              </button>

              {!payoutSettings.payoutMethod && (
                <p className="text-amber-400 text-xs text-center">Nejdříve nastav platební údaje</p>
              )}
            </div>
          </div>

          {/* Payout Settings */}
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-semibold">Platební údaje</h2>
              <button
                onClick={() => setShowPayoutForm(!showPayoutForm)}
                className="p-2 rounded-lg bg-dark-800 hover:bg-dark-700 transition-colors"
              >
                <Settings size={18} className="text-dark-400" />
              </button>
            </div>

            {!showPayoutForm ? (
              // Zobrazení aktuálních údajů
              <div className="space-y-3">
                {payoutSettings.payoutMethod ? (
                  <>
                    <div className="flex items-center space-x-3 p-3 rounded-xl bg-dark-800/50">
                      <CreditCard size={20} className="text-primary-400" />
                      <div>
                        <p className="text-white font-medium">
                          {payoutSettings.payoutMethod === 'paypal' ? 'PayPal' : 'Bankovní převod'}
                        </p>
                        <p className="text-dark-400 text-sm">
                          {payoutSettings.payoutMethod === 'paypal'
                            ? payoutSettings.paypalEmail
                            : payoutSettings.bankIban || payoutSettings.bankAccountNumber}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowPayoutForm(true)}
                      className="text-primary-400 text-sm hover:underline"
                    >
                      Změnit platební údaje
                    </button>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-dark-400 mb-3">Zatím nemáš nastavené platební údaje</p>
                    <button
                      onClick={() => setShowPayoutForm(true)}
                      className="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors"
                    >
                      Nastavit
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // Formulář pro úpravu
              <div className="space-y-4">
                {/* Výběr metody */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => setPayoutSettings(prev => ({ ...prev, payoutMethod: 'paypal' }))}
                    className={`flex-1 py-3 rounded-xl border transition-colors ${
                      payoutSettings.payoutMethod === 'paypal'
                        ? 'border-primary-500 bg-primary-500/10 text-white'
                        : 'border-dark-700 bg-dark-800 text-dark-400 hover:border-dark-600'
                    }`}
                  >
                    PayPal
                  </button>
                  <button
                    onClick={() => setPayoutSettings(prev => ({ ...prev, payoutMethod: 'bank' }))}
                    className={`flex-1 py-3 rounded-xl border transition-colors ${
                      payoutSettings.payoutMethod === 'bank'
                        ? 'border-primary-500 bg-primary-500/10 text-white'
                        : 'border-dark-700 bg-dark-800 text-dark-400 hover:border-dark-600'
                    }`}
                  >
                    Bankovní účet
                  </button>
                </div>

                {/* PayPal formulář */}
                {payoutSettings.payoutMethod === 'paypal' && (
                  <div>
                    <label className="block text-dark-400 text-sm mb-2">PayPal email</label>
                    <input
                      type="email"
                      value={payoutSettings.paypalEmail}
                      onChange={(e) => setPayoutSettings(prev => ({ ...prev, paypalEmail: e.target.value }))}
                      placeholder="vas@email.cz"
                      className="w-full px-4 py-3 rounded-xl bg-dark-800 border border-dark-700 text-white placeholder-dark-500 focus:outline-none focus:border-primary-500"
                    />
                  </div>
                )}

                {/* Bankovní formulář */}
                {payoutSettings.payoutMethod === 'bank' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-dark-400 text-sm mb-2">Jméno majitele účtu</label>
                      <input
                        type="text"
                        value={payoutSettings.bankAccountHolder}
                        onChange={(e) => setPayoutSettings(prev => ({ ...prev, bankAccountHolder: e.target.value }))}
                        placeholder="Jan Novák"
                        className="w-full px-4 py-3 rounded-xl bg-dark-800 border border-dark-700 text-white placeholder-dark-500 focus:outline-none focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-dark-400 text-sm mb-2">Číslo účtu (CZ formát)</label>
                      <input
                        type="text"
                        value={payoutSettings.bankAccountNumber}
                        onChange={(e) => setPayoutSettings(prev => ({ ...prev, bankAccountNumber: e.target.value }))}
                        placeholder="123456789/0100"
                        className="w-full px-4 py-3 rounded-xl bg-dark-800 border border-dark-700 text-white placeholder-dark-500 focus:outline-none focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-dark-400 text-sm mb-2">IBAN (volitelné)</label>
                      <input
                        type="text"
                        value={payoutSettings.bankIban}
                        onChange={(e) => setPayoutSettings(prev => ({ ...prev, bankIban: e.target.value }))}
                        placeholder="CZ65 0100 0000 0012 3456 7891"
                        className="w-full px-4 py-3 rounded-xl bg-dark-800 border border-dark-700 text-white placeholder-dark-500 focus:outline-none focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-dark-400 text-sm mb-2">SWIFT/BIC (volitelné)</label>
                      <input
                        type="text"
                        value={payoutSettings.bankSwift}
                        onChange={(e) => setPayoutSettings(prev => ({ ...prev, bankSwift: e.target.value }))}
                        placeholder="KOMBCZPP"
                        className="w-full px-4 py-3 rounded-xl bg-dark-800 border border-dark-700 text-white placeholder-dark-500 focus:outline-none focus:border-primary-500"
                      />
                    </div>
                  </div>
                )}

                {/* Tlačítka */}
                <div className="flex space-x-3 pt-2">
                  <button
                    onClick={() => setShowPayoutForm(false)}
                    className="flex-1 py-3 rounded-xl border border-dark-700 text-dark-400 hover:text-white hover:border-dark-600 transition-colors"
                  >
                    Zrušit
                  </button>
                  <button
                    onClick={handleSavePayoutSettings}
                    disabled={saving || !payoutSettings.payoutMethod}
                    className="flex-1 py-3 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                  >
                    {saving ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        <span>Ukládám...</span>
                      </>
                    ) : (
                      <span>Uložit</span>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent Commissions */}
        <div className="glass rounded-2xl p-6 mb-8">
          <h2 className="text-white font-semibold mb-4">Poslední provize</h2>

          {commissions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-dark-500">Zatím žádné provize. Začni sdílet svůj odkaz!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-dark-400 text-sm border-b border-dark-700">
                    <th className="text-left py-3 px-4">Datum</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-right py-3 px-4">Provize</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.slice(0, 10).map((commission) => (
                    <tr key={commission.id} className="border-b border-dark-700/50">
                      <td className="py-3 px-4 text-dark-300">{formatDate(commission.created_at)}</td>
                      <td className="py-3 px-4">{getStatusBadge(commission.status)}</td>
                      <td className="py-3 px-4 text-green-400 text-right font-medium">+{commission.amount} Kč</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Payout History */}
        {payouts.length > 0 && (
          <div className="glass rounded-2xl p-6">
            <h2 className="text-white font-semibold mb-4">Historie výplat</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-dark-400 text-sm border-b border-dark-700">
                    <th className="text-left py-3 px-4">Datum</th>
                    <th className="text-left py-3 px-4">Metoda</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-right py-3 px-4">Částka</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((payout) => (
                    <tr key={payout.id} className="border-b border-dark-700/50">
                      <td className="py-3 px-4 text-dark-300">{formatDate(payout.requested_at)}</td>
                      <td className="py-3 px-4 text-white">
                        {payout.payout_method === 'paypal' ? 'PayPal' : 'Banka'}
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(payout.status)}</td>
                      <td className="py-3 px-4 text-white text-right font-medium">{payout.amount} Kč</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="mt-8 text-center">
          <p className="text-dark-500 text-sm">
            Získáváš 20% provizi z každé platby uživatelů, které přivedeš
          </p>
        </div>
      </div>
    </div>
  );
};

export default AffiliateDashboard;
